import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import TimeSpan from "../../utils/time-span";

const DurationSlider: React.FC<{ value: number; timeFrom: TimeSpan; onSliderChange: (newValue: number) => void }> = ({
    value,
    timeFrom,
    onSliderChange,
}) => {
    const { t } = useTranslation();

    const matchesSmUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

    const [isMiniStepModeActive, setIsMiniStepModeActive] = useState(false);

    useEffect(() => {
        const keyDownHandler = (event: KeyboardEvent) => {
            if (event.ctrlKey && event.shiftKey) {
                setIsMiniStepModeActive(true);
            }
        };

        const keyUpHandler = (event: KeyboardEvent) => {
            if (!event.ctrlKey || !event.shiftKey) {
                setIsMiniStepModeActive(false);
            }
        };
        document.addEventListener("keydown", keyDownHandler);
        document.addEventListener("keyup", keyUpHandler);
        return () => {
            document.removeEventListener("keyup", keyUpHandler);
        };
    }, []);

    const handleChangeDuration = (_: Event, newValue: number | number[]) => {
        if (typeof newValue === "number") {
            onSliderChange(roundSliderToStep(valueToDuration(newValue)));
        }
    };

    const roundSliderToStep = (minutes: number) => {
        if (minutes < 120) {
            return Math.round(minutes / 15) * 15; // 0 - 2 hours 15 minute steps
        } else if (minutes < 360) {
            return Math.round(minutes / 30) * 30; // 2 - 6 hours 30 minute steps
        } else if (minutes < 720) {
            return Math.round(minutes / 45) * 45; // 6 - 12 hours 45 minute steps
        }
        return Math.round(minutes / 90) * 90; // 12 - 24 hours 90 minute steps
    };

    const roundButtonToStep = (minutes: number) => {
        if (isMiniStepModeActive) {
            return Math.round(minutes / 5) * 5;
        }
        return Math.round(minutes / 15) * 15;
    };

    const formatTime = (t: { hours: number; minutes: number }) =>
        `${t.hours.toString().padStart(2, "0")}:${t.minutes.toString().padStart(2, "0")}`;

    const formatDuration = (minutes: number) => {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return h > 0 ? `${h}h${m > 0 ? ` ${m}m` : ""}` : `${m}m`;
    };

    const valueToDuration = (sliderValue: number) => {
        if (sliderValue <= 25) {
            return sliderValue * (120 / 25); // 0% - 25% slider = 0 - 2 hours in 15-minute steps
        } else if (sliderValue <= 50) {
            return ((sliderValue - 25) * (360 - 120)) / 25 + 120; // 25% - 50% slider = 2 - 6 hours in 30-minute steps
        } else if (sliderValue <= 75) {
            return ((sliderValue - 50) * (720 - 360)) / 25 + 360; // 50% - 75% slider = 6 - 12 hours in 45-minute steps
        } else {
            return ((sliderValue - 75) * (1440 - 720)) / 25 + 720; // 75% - 100% slider = 12 - 24 hours in 90-minute steps
        }
    };

    const durationToSliderValue = (duration: number) => {
        if (duration <= 120) {
            return (duration / 120) * 25;
        } else if (duration <= 360) {
            return ((duration - 120) / (360 - 120)) * 25 + 25;
        } else if (duration <= 720) {
            return ((duration - 360) / (720 - 360)) * 25 + 50;
        } else {
            return ((duration - 720) / (1440 - 720)) * 25 + 75;
        }
    };

    const increase = () => onSliderChange(Math.min(24 * 60, roundButtonToStep(value + (isMiniStepModeActive ? 5 : 15))));
    const decrease = () =>
        onSliderChange(Math.max(isMiniStepModeActive ? 5 : 15, roundButtonToStep(value - (isMiniStepModeActive ? 5 : 15))));

    const endTime = useMemo(() => {
        const totalMinutes = timeFrom.hours * 60 + timeFrom.minutes + value;
        const daysLater = Math.floor(totalMinutes / (24 * 60)); // how many times wrapped
        const wrapped = totalMinutes % (24 * 60);

        return {
            time: { hours: Math.floor(wrapped / 60), minutes: wrapped % 60 },
            daysLater,
        };
    }, [timeFrom, value]);

    return (
        <React.Fragment>
            <Grid item sm={12} lg={6} pl={3}>
                <Typography variant="subtitle2">{t("scheduling-duration")}</Typography>
                <Box display="flex" alignItems="center" gap={0.5} pt={1}>
                    <Tooltip
                        title={isMiniStepModeActive ? t("scheduling-duration-mini-decrease") : t("scheduling-duration-decrease")}
                    >
                        <span>
                            <IconButton onClick={decrease} disabled={isMiniStepModeActive ? value <= 5 : value <= 15} sx={{ pt: 0.2 }}>
                                <RemoveIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Box flexGrow={1} position="relative">
                        {matchesSmUp ? (
                            <Slider
                                value={durationToSliderValue(value)}
                                min={2}
                                max={100} // working in percents here for non linear scale
                                onChange={handleChangeDuration}
                                sx={{
                                    "& .MuiSlider-thumb": {
                                        "&::after": {
                                            content: `"${formatDuration(value)}"`,
                                            position: "absolute",
                                            top: 20,
                                            transform: "translateX(-50%)",
                                            fontSize: "small",
                                            whiteSpace: "nowrap",
                                            display: "flex",
                                            justifyContent: "center",
                                        },
                                    },
                                }}
                            />
                        ) : (
                            <Typography>{formatDuration(value)}</Typography>
                        )}
                    </Box>
                    <Tooltip
                        title={isMiniStepModeActive ? t("scheduling-duration-mini-increase") : t("scheduling-duration-increase")}
                    >
                        <span>
                            <IconButton onClick={increase} disabled={value >= 24 * 60} sx={{ pt: 0.2 }}>
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Grid>

            <Grid item sm={12} lg={2}>
                <Typography variant="subtitle2">{t("scheduling-end-time")}</Typography>
                <Typography variant="body2" sx={{ minWidth: 50, paddingTop: 1, fontSize: "1rem" }}>
                    {formatTime(endTime.time)}
                    {endTime.daysLater > 0 && ` (+${endTime.daysLater})`}
                </Typography>
            </Grid>
        </React.Fragment>
    );
};

export default DurationSlider;